import React, { useMemo, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import { isMobileScreen } from '@apply/ui';
import { Button, ButtonTheme, Panel, PanelBorderRadius, PanelTheme, EmailBox, PasswordBox } from '@apply/components';
import { Footer } from '../brokerPortalV2/common_v2/components/Footer';
import { LoginApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { LogoLink } from '../common/components/LogoLink';
import store, { brokerActions } from '@apply/adminStore';
import { logApplicationInsightsTrace } from '../common/SharedFunctions';
import { useWaitOverlayStore } from '@apply/components';

interface LoginProps {
    setAdmin: (v: boolean) => void;
    setSuper: (v: boolean) => void;
    setBroker: (v: boolean) => void;
    setUserId: (id: string) => void;
    updateAuthentication: (authenticated: boolean, cb?: () => void) => void;
    bfiUser?: any;
}

export const Login: React.FC<LoginProps> = ({ setAdmin, setSuper, setBroker, setUserId, updateAuthentication }) => {
    const { setWaiting } = useWaitOverlayStore();
    const location = useLocation();
    const navigate = useNavigate();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const requestedEmail = query.get('email') ?? '';

    const [email, setEmail] = useState<string>(requestedEmail);
    const [password, setPassword] = useState<string>('');
    const [isEmailValid, set_isEmailValid] = useState<boolean>(false);
    const [isPasswordValid, set_isPasswordValid] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [attempts, setAttempts] = useState<number>(1);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [authenticated, setAuthenticated] = useState<boolean>(false);

    const readyToSubmit = () => {
        return isPasswordValid && isEmailValid;
    };

    useEffect(() => {
        // check existing session
        try {
            const loginApi = new LoginApi.LoginApi(applicationInsights);
            const auth = loginApi.isAuthenticated();
            if (auth) {
                setAuthenticated(true);
            }
        } catch (e) {
            // ignore
        }
        const t = setTimeout(() => setIsLoaded(true), 1000);
        return () => {
            clearTimeout(t);
        };
    }, []);

    const onEnterKey = () => {
        if (!submitting && readyToSubmit()) {
            handleLogin();
        }
    };

    const handleLogin = async () => {
        const loginApi = new LoginApi.LoginApi(applicationInsights);
        if (!readyToSubmit()) {
            setError(true);
            setErrorMessage('Fill out form completely');
            return;
        }

        setSubmitting(true);
        try {
            let ipAddressResult = '';
            const ipAddressResponse = await loginApi.getIpAddress();
            if (ipAddressResponse?.statusCode === 200) {
                ipAddressResult = ipAddressResponse.result;
            }
            const response = await loginApi.login({ email, password, ipaddress: ipAddressResult });
            // increment attempts
            setAttempts((prev) => prev + 1);
            if (response && response.statusCode === 200) {
                setWaiting(true, 'Logging you in...');
                logApplicationInsightsTrace(`${email} has logged in successfully.`);
                setTimeout(() => {
                    const { isSuper, admin, isBroker, brokerId, userId } = response;
                    setUserId(userId);
                    if (admin === true) {
                        setAdmin(true);
                        setSuper(isSuper);
                    } else {
                        setAdmin(false);
                        if (isBroker === true) {
                            setBroker(isBroker);
                            const { dispatch } = store;
                            dispatch(brokerActions.setBrokerId({ brokerId }));
                        }
                    }
                    updateAuthentication(true);
                    setAuthenticated(true);
                    window.location.href = '/';
                }, 1000);
                setError(false);
                setErrorMessage('');
            } else if (response && response.statusCode !== 200) {
                setSubmitting(false);
                setError(true);
                setErrorMessage(response?.errors || response?.err);
            } else {
                setSubmitting(false);
                setError(true);
                setErrorMessage('Could Not Contact Server');
            }
        } catch (err: any) {
            setSubmitting(false);
            setError(true);
            setErrorMessage(err?.message ?? 'Login failed');
        }
    };

    if (!isLoaded) {
        return <></>;
    }

    if (authenticated) {
        window.location.href = '/';
    }

    const panelChildren = () => {
        return (
            <>
                <div className={`preAuthenticationLogoLink ${isMobileScreen() ? 'mobile' : ''}`}>
                    <LogoLink />
                </div>
                <main>
                    <h2>Log In</h2>
                    <br />
                    <div className='login_register-fields'>
                        <EmailBox
                            id='emailAddress'
                            data-testid='login-page-email-address'
                            name='emailAddress'
                            value={email}
                            onChange={(value: string) => setEmail(value)}
                            onEnterKey={onEnterKey}
                            onValidationEvent={(isValid) => set_isEmailValid(isValid)}
                            label='Email Address'
                            required={true}
                        />
                        <PasswordBox
                            id='password'
                            data-testid='login-page-password'
                            name='password'
                            value={password}
                            onChange={(value) => setPassword(value)}
                            onEnterKey={onEnterKey}
                            onValidationEvent={(isValid) => set_isPasswordValid(isValid)}
                            label='Password'
                            required={true}
                        />
                    </div>
                    {error && (
                        <div>
                            <Typography style={{ color: 'red' }}>{errorMessage}</Typography>
                        </div>
                    )}
                    {attempts > 3 && (
                        <div
                            data-testid='login-page-max-attempts-message'
                            className='login maxAttemptsExceeded'
                        >
                            <span style={{ paddingTop: '16px' }}>Would you like to</span>
                            <Button
                                id='attempt-exceeded-passwordreset-button'
                                key='attempt-exceeded-passwordreset-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate('/forgotpassword')}
                                width='165px'
                            >
                                Reset your password?
                            </Button>
                        </div>
                    )}
                    <div className='login primaryActions'>
                        <Button
                            id='primaryButton'
                            theme={ButtonTheme.Primary}
                            width='80%'
                            disabled={!isEmailValid || !isPasswordValid || submitting}
                            handleClick={handleLogin}
                        >
                            {submitting ? 'Signing in…' : 'Sign In'}
                        </Button>
                    </div>
                    <div className='login secondaryActions'>
                        <div style={{ float: 'left', width: 'auto' }}>
                            <Button
                                id='register-button'
                                key='register-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate(`/forgotPassword?email=${encodeURIComponent(email)}`)}
                                width='100px'
                            >
                                Can't log in?
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {isMobileScreen() ? (
                        <div className='authenticationContainer mobile'>{panelChildren()}</div>
                    ) : (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width={'90%'}
                            >
                                <div className='authenticationContainer-desktop-desktopWrapper'>{panelChildren()}</div>
                            </Panel>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};
